import { isEqual, keys, pick } from 'lodash-es';

import { pickValid } from '@looko-corp/acloset-react-kit/src/utils';

import { acError } from '../ASentry/Log';
import { ChannelService } from './ChannelService';

/**
 * 채널톡 서비스 관리를 위한 싱글톤.
 */
export class ChannelTalkService {
  private static instance: ChannelService | null = null;
  private static state: {
    status: 'shutdown' | 'booting' | 'booted';
    shopID?: string;
  } = { status: 'shutdown' };

  private static onMessengerVisibleChanged?: (visible: boolean) => void;

  private static get() {
    if (!this.instance) {
      this.instance = new ChannelService();
      this.instance.onHideMessenger(() => {
        ChannelTalkService.onMessengerVisibleChanged?.(false);
      });
      this.instance.onShowMessenger(() => {
        ChannelTalkService.onMessengerVisibleChanged?.(true);
      });
    }
    return this.instance;
  }

  static shutdown() {
    this.instance?.shutdown();
    this.state = { status: 'shutdown' };
  }

  static showMessengerForGuest(deviceID: string) {
    const service = this.get();

    switch (this.state.status) {
      case 'shutdown': {
        this.state = {
          status: 'booting',
          shopID: undefined,
        };

        service.boot(
          {
            pluginKey: import.meta.env.VITE_CHANNEL_TALK_PLUGIN_KEY,
            memberId: deviceID,
          },
          err => {
            if (err) {
              acError(`Failed to boot ChannelTalk: ${err.message}`, err);
            } else {
              if (this.state.status === 'booting' && this.state.shopID === undefined) {
                service.hideChannelButton();
                service.showMessenger();
                this.state.status = 'booted';
              }
            }
          },
        );
        break;
      }
      case 'booting': {
        break;
      }
      case 'booted': {
        service.showMessenger();
        break;
      }
    }
  }

  static bootForUser(shopID: string, profile: { name: string; email: string; mobileNumber?: string }) {
    if (!shopID) {
      return;
    }

    if (this.state.shopID !== shopID) {
      this.instance?.shutdown();
    }

    switch (this.state.status) {
      case 'shutdown': {
        this.state = {
          status: 'booting',
          shopID: shopID,
        };

        const service = this.get();
        service.boot(
          {
            pluginKey: import.meta.env.VITE_CHANNEL_TALK_PLUGIN_KEY,
            memberId: shopID,
          },
          (err, user) => {
            if (err) {
              acError(`Failed to boot ChannelTalk: ${err.message}`, err);
            } else {
              if (user) {
                const currentProfile = pickValid(profile);
                const validTalkProfile = pick(user.profile, keys(currentProfile));

                // 무의미한 프로필 업데이트 방지.
                if (user.profile == null || !isEqual(currentProfile, validTalkProfile)) {
                  this.instance?.updateUser({
                    profile: currentProfile,
                  });
                }
              }

              if (this.state.status === 'booting' && this.state.shopID === shopID) {
                service.hideChannelButton();
                this.state.status = 'booted';
              }
            }
          },
        );
        break;
      }
      case 'booting':
      case 'booted': {
        break;
      }
    }
  }

  static showMessenger() {
    this.instance?.showMessenger();
  }

  static hideMessenger() {
    this.instance?.hideMessenger();
  }

  static subscribeMessengerVisibility(callback: (visible: boolean) => void) {
    this.onMessengerVisibleChanged = callback;
    return () => {
      this.onMessengerVisibleChanged = undefined;
    };
  }
}
